import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { InfoTip } from "@/components/ui/info-tip";
import {
  ChevronLeft, Plus, Minus, Trash2, ZoomIn, ZoomOut, Maximize2,
  ClipboardList, Copy, Footprints, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { formatInches } from "./wall-framer";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type StringerBoard = "2x10" | "2x12";

export interface StairPlan {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  totalRise: number;      // floor-to-floor finished height (in)
  targetRiser: number;    // desired riser height (in) — drives the riser count
  treadDepth: number;     // run per tread, excluding nosing (in)
  treadThickness: number; // finished tread stock thickness (in) — sets the bottom drop
  nosing: number;         // tread nosing overhang (in)
  stairWidth: number;     // stair width (in)
  stringerCount: number;
  stringerBoard: StringerBoard;
}

const BOARD_WIDTH: Record<StringerBoard, number> = { "2x10": 9.25, "2x12": 11.25 };
const MIN_THROAT = 5; // common minimum remaining (throat) depth behind a notch (in)

function boardWidthOf(plan: StairPlan): number {
  return BOARD_WIDTH[plan.stringerBoard] ?? 11.25;
}
function treadThicknessOf(plan: StairPlan): number {
  return plan.treadThickness ?? 1; // back-compat for plans saved before this field
}

function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}
function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

// ─── Stair math ─────────────────────────────────────────────────────────────────

export interface StairCalc {
  numRisers: number;
  riserHeight: number;     // unit rise (square setting)
  numTreads: number;
  treadDepth: number;      // unit run (square setting)
  totalRun: number;
  stringerLength: number;  // along the slope
  angleDeg: number;
  twoRplusT: number;
  // Cut-ready values:
  notchDepth: number;      // perpendicular depth cut into the board per notch
  throat: number;          // material left behind a notch
  throatOK: boolean;
  bottomDrop: number;      // amount cut off the bottom = tread thickness
  firstRiserCut: number;   // bottom plumb cut height on the stringer (unit rise − drop)
  topRiserCut: number;     // top riser cut height on the stringer
  boardLengthFt: number;   // recommended stock length
  warnings: string[];
  ok: boolean;
}

/** Standard residential stair layout from a measured total rise. IRC 2018 limits. */
export function calcStairs(plan: StairPlan): StairCalc {
  const target = plan.targetRiser > 0 ? plan.targetRiser : 7.5;
  const numRisers = Math.max(1, Math.round(plan.totalRise / target));
  const riserHeight = numRisers > 0 ? plan.totalRise / numRisers : 0;
  const numTreads = Math.max(0, numRisers - 1); // top riser lands on the finished floor
  const treadDepth = plan.treadDepth;
  const totalRun = numTreads * treadDepth;
  const stringerLength = Math.sqrt(plan.totalRise ** 2 + totalRun ** 2);
  const diag = Math.sqrt(riserHeight ** 2 + treadDepth ** 2) || 1;
  const angleDeg = (Math.atan2(plan.totalRise, totalRun || 1) * 180) / Math.PI;
  const twoRplusT = 2 * riserHeight + treadDepth;

  // Cut geometry.
  const boardW = boardWidthOf(plan);
  const drop = treadThicknessOf(plan);
  const notchDepth = (riserHeight * treadDepth) / diag; // perpendicular notch depth
  const throat = boardW - notchDepth;
  const throatOK = throat >= MIN_THROAT;
  // Bottom is dropped by one tread thickness (tread sits on the notch); the top
  // riser is taller by the same amount so the run of notches still spans the full
  // rise and every finished step (tread-top to tread-top) equals the unit rise.
  const firstRiserCut = Math.max(0, riserHeight - drop);
  const topRiserCut = riserHeight + drop;
  const boardLengthFt = recommendBoardFt(stringerLength);

  const warnings: string[] = [];
  if (riserHeight > 7.75) warnings.push(`Riser ${formatInches(riserHeight)} exceeds 7¾″ max — add a riser`);
  if (riserHeight > 0 && riserHeight < 4) warnings.push(`Riser ${formatInches(riserHeight)} is below the 4″ min`);
  if (treadDepth < 10) warnings.push(`Tread ${formatInches(treadDepth)} is below the 10″ min`);
  if (twoRplusT < 24 || twoRplusT > 25) warnings.push(`2R+T = ${round1(twoRplusT)}″ (ideal 24–25″)`);
  if (!throatOK) warnings.push(`Throat ${formatInches(throat)} < ${MIN_THROAT}″ — use a wider board or shallower pitch`);

  const ok = riserHeight <= 7.75 && riserHeight >= 4 && treadDepth >= 10 && throatOK;
  return {
    numRisers, riserHeight, numTreads, treadDepth, totalRun, stringerLength, angleDeg,
    twoRplusT, notchDepth, throat, throatOK, bottomDrop: drop, firstRiserCut, topRiserCut,
    boardLengthFt, warnings, ok,
  };
}

// ─── Cutlist ──────────────────────────────────────────────────────────────────

const STD_LENGTHS_FT = [8, 10, 12, 14, 16, 20];

function recommendBoardFt(inches: number): number {
  const ft = inches / 12;
  return STD_LENGTHS_FT.find((l) => l >= ft + 0.5) ?? Math.ceil(ft);
}

export interface StairCutPiece {
  id: string;
  label: string;
  detail: string;
  qty: number;
}

export function generateStairCutlist(plan: StairPlan): {
  stringers: StairCutPiece[];
  treads: StairCutPiece[];
  risers: StairCutPiece[];
} {
  const c = calcStairs(plan);
  const treadMaterial = plan.treadDepth + plan.nosing;
  return {
    stringers: c.numRisers > 0 ? [{
      id: "str",
      label: `${plan.stringerCount}× Stringer (${plan.stringerBoard})`,
      detail: `${formatInches(c.stringerLength)} along slope · cut from ${c.boardLengthFt}′ stock`,
      qty: plan.stringerCount,
    }] : [],
    treads: c.numTreads > 0 ? [{
      id: "trd",
      label: `${c.numTreads}× Tread`,
      detail: `${formatInches(plan.stairWidth)} wide × ${formatInches(treadMaterial)} deep (incl. ${formatInches(plan.nosing)} nosing)`,
      qty: c.numTreads,
    }] : [],
    risers: c.numRisers > 0 ? [{
      id: "rsr",
      label: `${c.numRisers}× Riser`,
      detail: `${formatInches(plan.stairWidth)} wide × ${formatInches(c.riserHeight)} tall`,
      qty: c.numRisers,
    }] : [],
  };
}

// ─── localStorage store ───────────────────────────────────────────────────────

const STORAGE_KEY = "quotrak:stair-plans";
const PLANS_EVENT = "quotrak-stair-plans-updated";

function readPlans(): StairPlan[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StairPlan[]) : [];
  } catch {
    return [];
  }
}

function writePlans(plans: StairPlan[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  window.dispatchEvent(new Event(PLANS_EVENT));
}

function useStairPlans() {
  const [plans, setPlans] = useState<StairPlan[]>(readPlans);

  useEffect(() => {
    const handler = () => setPlans(readPlans());
    window.addEventListener(PLANS_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(PLANS_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const addPlan = useCallback((init: Omit<StairPlan, "id" | "createdAt" | "updatedAt">) => {
    const now = Date.now();
    const plan: StairPlan = { ...init, id: uid(), createdAt: now, updatedAt: now };
    writePlans([...readPlans(), plan]);
    return plan.id;
  }, []);

  const updatePlan = useCallback((id: string, updates: Partial<Omit<StairPlan, "id" | "createdAt" | "updatedAt">>) => {
    writePlans(readPlans().map((p) => (p.id === id ? { ...p, ...updates, updatedAt: Date.now() } : p)));
  }, []);

  const deletePlan = useCallback((id: string) => {
    writePlans(readPlans().filter((p) => p.id !== id));
  }, []);

  return { plans, addPlan, updatePlan, deletePlan };
}

// ─── Inputs ─────────────────────────────────────────────────────────────────────

const iconBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: "3rem", height: "3rem", flexShrink: 0,
  border: "1px solid hsl(var(--border))", borderRadius: "0.5rem",
  backgroundColor: "hsl(var(--card))", cursor: "pointer",
  color: "hsl(var(--foreground))",
};

const unitLabelStyle: React.CSSProperties = {
  position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)",
  fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", pointerEvents: "none",
};

/** Feet + inches stepper (for larger measurements like total rise). */
function DimInput({
  label, valueInches, onChange, min = 0, max = 9999,
}: {
  label: string;
  valueInches: number;
  onChange: (inches: number) => void;
  min?: number;
  max?: number;
}) {
  const [feetStr, setFeetStr] = useState(String(Math.floor(valueInches / 12)));
  const [inchStr, setInchStr] = useState(String(round2(valueInches % 12)));

  useEffect(() => {
    setFeetStr(String(Math.floor(valueInches / 12)));
    setInchStr(String(round2(valueInches % 12)));
  }, [valueInches]);

  const clamp = (v: number) => Math.max(min, Math.min(max, v));
  const commit = (f: string, inc: string) => {
    const total = (parseFloat(f) || 0) * 12 + (parseFloat(inc) || 0);
    onChange(clamp(total));
  };
  const nudge = (dir: 1 | -1) => onChange(clamp(valueInches + dir));

  const cellStyle: React.CSSProperties = {
    height: "3rem", fontSize: "1.0625rem", fontWeight: 600,
    textAlign: "center", fontVariantNumeric: "tabular-nums",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))" }}>
        {label}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <button onClick={() => nudge(-1)} aria-label="Decrease" style={iconBtnStyle} className="hover:bg-accent transition-colors">
          <Minus style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
        <div style={{ flex: 1, display: "flex", gap: "0.4rem" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Input type="number" inputMode="decimal" value={feetStr}
              onChange={(e) => { setFeetStr(e.target.value); commit(e.target.value, inchStr); }} style={cellStyle} />
            <span style={unitLabelStyle}>ft</span>
          </div>
          <div style={{ position: "relative", flex: 1 }}>
            <Input type="number" inputMode="decimal" value={inchStr}
              onChange={(e) => { setInchStr(e.target.value); commit(feetStr, e.target.value); }} style={cellStyle} />
            <span style={unitLabelStyle}>in</span>
          </div>
        </div>
        <button onClick={() => nudge(1)} aria-label="Increase" style={iconBtnStyle} className="hover:bg-accent transition-colors">
          <Plus style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
      </div>
    </div>
  );
}

/** Single-field stepper in inches (for small dimensions / counts). */
function NumStepper({
  label, value, onChange, min = 0, max = 999, step = 0.25, unit = "in", integer = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  integer?: boolean;
}) {
  const [str, setStr] = useState(String(integer ? Math.round(value) : round2(value)));
  useEffect(() => { setStr(String(integer ? Math.round(value) : round2(value))); }, [value, integer]);

  const clamp = (v: number) => Math.max(min, Math.min(max, v));
  const cellStyle: React.CSSProperties = {
    height: "3rem", fontSize: "1.0625rem", fontWeight: 600,
    textAlign: "center", fontVariantNumeric: "tabular-nums",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))" }}>
        {label}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <button onClick={() => onChange(clamp(value - step))} aria-label="Decrease" style={iconBtnStyle} className="hover:bg-accent transition-colors">
          <Minus style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
        <div style={{ position: "relative", flex: 1 }}>
          <Input type="number" inputMode="decimal" value={str}
            onChange={(e) => { setStr(e.target.value); const v = parseFloat(e.target.value); if (isFinite(v)) onChange(clamp(v)); }}
            style={{ ...cellStyle, paddingRight: unit ? "2.25rem" : undefined }} />
          {unit && <span style={unitLabelStyle}>{unit}</span>}
        </div>
        <button onClick={() => onChange(clamp(value + step))} aria-label="Increase" style={iconBtnStyle} className="hover:bg-accent transition-colors">
          <Plus style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
      </div>
    </div>
  );
}

// ─── Stringer side-profile preview ──────────────────────────────────────────────

const COLOR_TREAD = "#0d9488";   // teal
const COLOR_RISER = "#ca8a04";   // amber
const COLOR_BOARD = "#e7d3ad";   // light wood fill
const COLOR_BOARD_EDGE = "#a16207";
const COLOR_DIM = "#44403c";
const COLOR_THROAT = "#b91c1c";  // red — structural

function StairPreview({ plan }: { plan: StairPlan }) {
  const c = useMemo(() => calcStairs(plan), [plan]);
  const [zoom, setZoom] = useState(1);

  const R = c.riserHeight;
  const T = c.treadDepth;
  const N = c.numRisers;
  const totalRise = plan.totalRise;
  const totalRun = c.totalRun;
  const hasRun = totalRun > 0 && N > 1;

  // Board strip geometry (math coords, y up, origin = floor at the first riser).
  // The whole notch run is dropped by one tread thickness: the tread notches land
  // at k·R − drop, so the bottom riser cut is short (R − drop) and the top riser
  // runs from the last notch up to the full rise (R + drop).
  const drop = c.bottomDrop;
  const diag = Math.sqrt(R * R + T * T) || 1;
  const cosT = T / diag;            // cos of stair angle
  const slopeM = T > 0 ? R / T : 0; // rise per run
  const W = boardWidthOf(plan);
  const vertOffset = W / (cosT || 1);            // vertical gap: peak line → back edge
  const backEdge = (x: number) => slopeM * x + R - drop - vertOffset;
  const topX = (N - 1) * T;
  const xb = slopeM > 0 ? Math.max(0, Math.min(topX, (vertOffset + drop - R) / slopeM)) : 0; // seat-cut length

  // Stringer cut profile (the actual notches), bottom-up.
  const sawPts: [number, number][] = [[0, 0]];
  for (let k = 1; k <= N - 1; k++) {
    sawPts.push([(k - 1) * T, k * R - drop]); // top of riser k
    sawPts.push([k * T, k * R - drop]);       // tread-k notch (horizontal seat)
  }
  sawPts.push([topX, N * R]);                  // top riser up to the upper floor

  const segments: { x1: number; y1: number; x2: number; y2: number; kind: "riser" | "tread" }[] = [];
  for (let k = 1; k <= N - 1; k++) {
    const baseX = (k - 1) * T;
    const yBot = k === 1 ? 0 : (k - 1) * R - drop;  // bottom riser starts at the floor
    const notchY = k * R - drop;
    segments.push({ x1: baseX, y1: yBot, x2: baseX, y2: notchY, kind: "riser" });
    segments.push({ x1: baseX, y1: notchY, x2: k * T, y2: notchY, kind: "tread" });
  }
  segments.push({ x1: topX, y1: N >= 2 ? (N - 1) * R - drop : 0, x2: topX, y2: N * R, kind: "riser" }); // top riser

  // Board polygon: seat cut (floor) + back edge + top plumb cut + sawtooth.
  const board: [number, number][] = [[0, 0]];
  if (xb > 0.01) board.push([xb, 0]);
  board.push([topX, Math.max(0, backEdge(topX))]);
  board.push([topX, totalRise]);
  for (let i = sawPts.length - 1; i >= 0; i--) board.push(sawPts[i]); // sawtooth back down to (0,0)

  // Throat is identical at every notch — show it at a mid-run notch, which has
  // open space around it, instead of cramming it onto the tiny first step.
  const midK = Math.max(1, Math.min(Math.max(1, N - 1), Math.round(N / 2)));
  const inX = midK * T, inY = midK * R - drop;  // inside corner of the chosen notch
  // Toward the back edge along the perpendicular: dir = (sinθ, −cosθ) = (R/diag, −T/diag).
  const thrEndX = inX + (R / diag) * c.throat;
  const thrEndY = inY - (T / diag) * c.throat;

  // Layout bounds (no negative y — seat cut clips at the floor).
  const xMax = Math.max(totalRun, T);
  const yMax = totalRise;
  const padL = 66, padR = 76, padT = 26, padB = 54;
  const vbW = xMax + padL + padR;
  const vbH = yMax + padT + padB;
  const sx = (x: number) => padL + x;
  const sy = (y: number) => padT + (yMax - y);
  const font = Math.max(7, totalRise * 0.05);
  const small = font * 0.82;
  const dimStroke = 0.6;
  const clampX = (x: number) => Math.max(4, Math.min(vbW - 4, x));

  return (
    <div>
      <div style={{ position: "relative", borderRadius: "0.625rem", overflow: "hidden", border: "1px solid hsl(var(--border))", backgroundColor: "#f8f7f4" }}>
        <div style={{ position: "absolute", top: "0.5rem", left: "0.5rem", zIndex: 2, fontSize: "0.625rem", fontWeight: 700, letterSpacing: "0.05em", color: "#57534e", backgroundColor: "rgba(255,255,255,0.8)", padding: "0.15rem 0.4rem", borderRadius: "0.3rem" }}>
          {plan.stringerBoard} STRINGER · SIDE VIEW
        </div>
        <div style={{ position: "absolute", top: "0.5rem", right: "0.5rem", zIndex: 2, display: "flex", gap: "0.25rem" }}>
          <button onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10))} aria-label="Zoom out" style={zoomBtnStyle}>
            <ZoomOut style={{ width: "1rem", height: "1rem" }} />
          </button>
          <button onClick={() => setZoom(1)} aria-label="Fit" style={zoomBtnStyle}>
            <Maximize2 style={{ width: "1rem", height: "1rem" }} />
          </button>
          <button onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.5) * 10) / 10))} aria-label="Zoom in" style={zoomBtnStyle}>
            <ZoomIn style={{ width: "1rem", height: "1rem" }} />
          </button>
        </div>

        <div style={{ width: "100%", aspectRatio: "4 / 3", overflow: "auto", WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
          <div style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%`, minWidth: "100%", minHeight: "100%" }}>
            <svg viewBox={`0 0 ${vbW} ${vbH}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
              <defs>
                <pattern id="st-grid" width="12" height="12" patternUnits="userSpaceOnUse">
                  <path d="M 12 0 L 0 0 0 12" fill="none" stroke="#00000010" strokeWidth="0.4" />
                </pattern>
              </defs>
              <rect x={0} y={0} width={vbW} height={vbH} fill="url(#st-grid)" />

              {/* Floors */}
              <line x1={sx(0) - 10} y1={sy(0)} x2={sx(Math.max(totalRun, T)) + 6} y2={sy(0)} stroke="#78716c" strokeWidth={1} strokeDasharray="3 2" />
              <line x1={sx(topX) - 6} y1={sy(totalRise)} x2={sx(topX) + 14} y2={sy(totalRise)} stroke="#78716c" strokeWidth={1} strokeDasharray="3 2" />

              {/* Carriage board (to scale for the selected board width) */}
              <polygon points={board.map(([x, y]) => `${sx(x)},${sy(y)}`).join(" ")} fill={COLOR_BOARD} stroke={COLOR_BOARD_EDGE} strokeWidth={0.9} strokeLinejoin="round" />

              {/* Sawtooth treads + risers */}
              {segments.map((s, i) => (
                <line key={i} x1={sx(s.x1)} y1={sy(s.y1)} x2={sx(s.x2)} y2={sy(s.y2)}
                  stroke={s.kind === "tread" ? COLOR_TREAD : COLOR_RISER} strokeWidth={1.8} strokeLinecap="round" />
              ))}

              {/* Step numbers on each tread */}
              {Array.from({ length: c.numTreads }, (_, i) => {
                const k = i + 1;
                return (
                  <text key={`n${k}`} x={sx((k - 0.5) * T)} y={sy(k * R - drop) - small * 0.5} textAnchor="middle" fontSize={small} fontWeight={700} fill={COLOR_TREAD}>
                    {k}
                  </text>
                );
              })}

              {/* Throat (perpendicular notch → back edge), at a mid-run notch with room */}
              {hasRun && (
                <>
                  <line x1={sx(inX)} y1={sy(inY)} x2={sx(thrEndX)} y2={sy(thrEndY)} stroke={COLOR_THROAT} strokeWidth={dimStroke + 0.4} />
                  <circle cx={sx(inX)} cy={sy(inY)} r={1} fill={COLOR_THROAT} />
                  <text x={clampX(sx(thrEndX))} y={sy(thrEndY) + small * 1.5} textAnchor="middle" fontSize={small} fontWeight={700} fill={COLOR_THROAT}>
                    throat {formatInches(c.throat)}
                  </text>
                </>
              )}

              {/* Total rise (far left) */}
              <line x1={sx(0) - 42} y1={sy(0)} x2={sx(0) - 42} y2={sy(totalRise)} stroke={COLOR_DIM} strokeWidth={dimStroke} />
              <line x1={sx(0) - 45} y1={sy(0)} x2={sx(0) - 39} y2={sy(0)} stroke={COLOR_DIM} strokeWidth={dimStroke} />
              <line x1={sx(0) - 45} y1={sy(totalRise)} x2={sx(0) - 39} y2={sy(totalRise)} stroke={COLOR_DIM} strokeWidth={dimStroke} />
              <text x={clampX(sx(0) - 50)} y={sy(totalRise / 2)} textAnchor="middle" fontSize={font} fontWeight={700} fill={COLOR_DIM}
                transform={`rotate(-90, ${clampX(sx(0) - 50)}, ${sy(totalRise / 2)})`}>
                Rise {formatInches(totalRise)}
              </text>

              {/* Bottom riser cut (short by the tread thickness) — on the first riser */}
              <line x1={sx(0) - 16} y1={sy(0)} x2={sx(0) - 16} y2={sy(c.firstRiserCut)} stroke={COLOR_RISER} strokeWidth={dimStroke} />
              <line x1={sx(0) - 19} y1={sy(0)} x2={sx(0) - 13} y2={sy(0)} stroke={COLOR_RISER} strokeWidth={dimStroke} />
              <line x1={sx(0) - 19} y1={sy(c.firstRiserCut)} x2={sx(0) - 13} y2={sy(c.firstRiserCut)} stroke={COLOR_RISER} strokeWidth={dimStroke} />
              <text x={clampX(sx(0) - 22)} y={sy(c.firstRiserCut / 2)} textAnchor="middle" fontSize={small} fontWeight={700} fill={COLOR_RISER}
                transform={`rotate(-90, ${clampX(sx(0) - 22)}, ${sy(c.firstRiserCut / 2)})`}>
                btm {formatInches(c.firstRiserCut)}
              </text>

              {/* Top riser cut (tall by the tread thickness) — on the top riser */}
              <line x1={sx(topX) + 16} y1={sy((N - 1) * R - drop)} x2={sx(topX) + 16} y2={sy(N * R)} stroke={COLOR_RISER} strokeWidth={dimStroke} />
              <line x1={sx(topX) + 13} y1={sy((N - 1) * R - drop)} x2={sx(topX) + 19} y2={sy((N - 1) * R - drop)} stroke={COLOR_RISER} strokeWidth={dimStroke} />
              <line x1={sx(topX) + 13} y1={sy(N * R)} x2={sx(topX) + 19} y2={sy(N * R)} stroke={COLOR_RISER} strokeWidth={dimStroke} />
              <text x={clampX(sx(topX) + 24)} y={sy(N * R - c.topRiserCut / 2)} textAnchor="middle" fontSize={small} fontWeight={700} fill={COLOR_RISER}
                transform={`rotate(-90, ${clampX(sx(topX) + 24)}, ${sy(N * R - c.topRiserCut / 2)})`}>
                top {formatInches(c.topRiserCut)}
              </text>

              {/* Unit run T — above the first tread notch */}
              {hasRun && (
                <>
                  <line x1={sx(0)} y1={sy(R - drop) - small * 1.5} x2={sx(T)} y2={sy(R - drop) - small * 1.5} stroke={COLOR_TREAD} strokeWidth={dimStroke} />
                  <line x1={sx(0)} y1={sy(R - drop) - small * 1.9} x2={sx(0)} y2={sy(R - drop) - small * 1.1} stroke={COLOR_TREAD} strokeWidth={dimStroke} />
                  <line x1={sx(T)} y1={sy(R - drop) - small * 1.9} x2={sx(T)} y2={sy(R - drop) - small * 1.1} stroke={COLOR_TREAD} strokeWidth={dimStroke} />
                  <text x={sx(T / 2)} y={sy(R - drop) - small * 2.1} textAnchor="middle" fontSize={small} fontWeight={700} fill={COLOR_TREAD}>
                    T {formatInches(T)}
                  </text>
                </>
              )}

              {/* Total run (bottom) */}
              {hasRun && (
                <>
                  <line x1={sx(0)} y1={sy(0) + font * 1.7} x2={sx(totalRun)} y2={sy(0) + font * 1.7} stroke={COLOR_DIM} strokeWidth={dimStroke} />
                  <line x1={sx(0)} y1={sy(0) + font * 1.4} x2={sx(0)} y2={sy(0) + font * 2.0} stroke={COLOR_DIM} strokeWidth={dimStroke} />
                  <line x1={sx(totalRun)} y1={sy(0) + font * 1.4} x2={sx(totalRun)} y2={sy(0) + font * 2.0} stroke={COLOR_DIM} strokeWidth={dimStroke} />
                  <text x={sx(totalRun / 2)} y={sy(0) + font * 2.9} textAnchor="middle" fontSize={font} fontWeight={700} fill={COLOR_DIM}>
                    Run {formatInches(totalRun)}
                  </text>
                </>
              )}
            </svg>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem 0.875rem", marginTop: "0.625rem" }}>
        {[
          { c: COLOR_TREAD, l: "Treads / run" },
          { c: COLOR_RISER, l: "Risers / rise" },
          { c: COLOR_BOARD_EDGE, l: "Stringer (to scale)" },
          { c: COLOR_THROAT, l: "Throat" },
        ].map((x) => (
          <div key={x.l} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
            <span style={{ width: "0.7rem", height: "0.7rem", borderRadius: "0.15rem", backgroundColor: x.c, flexShrink: 0 }} />
            {x.l}
          </div>
        ))}
      </div>
    </div>
  );
}

const zoomBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: "1.875rem", height: "1.875rem", borderRadius: "0.375rem",
  border: "1px solid rgba(0,0,0,0.1)", backgroundColor: "rgba(255,255,255,0.9)",
  cursor: "pointer", color: "#44403c",
};

// ─── Results: tiles + code checks ───────────────────────────────────────────────

function ResultTiles({ calc }: { calc: StairCalc }) {
  const tiles: { value: string; label: string; warn?: boolean; tip?: string }[] = [
    { value: formatInches(calc.riserHeight), label: "Unit Rise", tip: "Total rise ÷ number of risers — the height of each step." },
    { value: formatInches(calc.treadDepth), label: "Unit Run", tip: "The tread depth you entered (run per step, excluding nosing)." },
    { value: String(calc.numRisers), label: "Risers", tip: "Total rise ÷ target riser height, rounded to the nearest whole riser." },
    { value: formatInches(calc.throat), label: "Throat", warn: !calc.throatOK, tip: "Perpendicular wood left behind the deepest notch — keeps the stringer strong." },
    { value: formatInches(calc.stringerLength), label: "Stringer", tip: "√(total rise² + total run²) — the board length along the slope." },
    { value: `${round1(calc.angleDeg)}°`, label: "Angle", tip: "atan(total rise ÷ total run) — the stair's slope." },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
      {tiles.map((t, i) => (
        <div key={i} style={{ backgroundColor: t.warn ? "#fef2f2" : "hsl(var(--muted))", border: t.warn ? "1px solid #fecaca" : "1px solid transparent", borderRadius: "0.5rem", padding: "0.625rem 0.4rem", textAlign: "center" }}>
          <div style={{ fontSize: "1.0625rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: t.warn ? "#b91c1c" : "hsl(var(--foreground))", lineHeight: 1.15 }}>{t.value}</div>
          <div style={{ fontSize: "0.625rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: "hsl(var(--muted-foreground))", marginTop: "0.2rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem" }}>
            {t.label}
            {t.tip && <InfoTip content={t.tip} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function CodeCheck({ calc }: { calc: StairCalc }) {
  if (calc.ok && calc.warnings.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.625rem 0.875rem", borderRadius: "0.5rem", backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0" }}>
        <CheckCircle2 style={{ width: "1.05rem", height: "1.05rem", color: "#059669", flexShrink: 0 }} />
        <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#065f46" }}>
          Within IRC limits · 2R+T = {round1(calc.twoRplusT)}″ · throat {formatInches(calc.throat)}
        </span>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", padding: "0.625rem 0.875rem", borderRadius: "0.5rem", backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
      {calc.warnings.map((w, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <AlertTriangle style={{ width: "1rem", height: "1rem", color: "#d97706", flexShrink: 0 }} />
          <span style={{ fontSize: "0.8125rem", color: "#92400e" }}>{w}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Cut sheet (how to actually mark & cut the stringer) ────────────────────────

function CutSheet({ plan }: { plan: StairPlan }) {
  const c = useMemo(() => calcStairs(plan), [plan]);
  const rows: { k: string; v: string }[] = [
    { k: "Framing square", v: `${formatInches(c.riserHeight)} rise × ${formatInches(c.treadDepth)} run` },
    { k: "Notches to mark", v: `${c.numRisers} (${c.numTreads} tread cuts)` },
    { k: "Bottom: drop / cut off", v: formatInches(c.bottomDrop) },
    { k: "Bottom riser cut", v: formatInches(c.firstRiserCut) },
    { k: "Top riser cut", v: formatInches(c.topRiserCut) },
    { k: "Throat (min " + MIN_THROAT + "″)", v: `${formatInches(c.throat)}${c.throatOK ? "" : "  ⚠"}` },
    { k: "Stringer length", v: `${formatInches(c.stringerLength)} (order ${c.boardLengthFt}′ ${plan.stringerBoard})` },
  ];
  return (
    <div>
      <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", borderBottom: "1px solid hsl(var(--border))", paddingBottom: "0.3rem", marginBottom: "0.4rem" }}>
        Cut Sheet
      </div>
      <ul style={{ display: "flex", flexDirection: "column", gap: "0.25rem", listStyle: "none", margin: 0, padding: 0 }}>
        {rows.map((r) => (
          <li key={r.k} style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", padding: "0.4rem 0.625rem", backgroundColor: "hsl(var(--muted))", borderRadius: "0.4rem" }}>
            <span style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{r.k}</span>
            <span style={{ fontSize: "0.875rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{r.v}</span>
          </li>
        ))}
      </ul>
      <p style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", lineHeight: 1.5, margin: "0.5rem 0 0" }}>
        The bottom riser is cut <strong>shorter</strong> and the top riser <strong>taller</strong> by one tread thickness (the “drop”), so once the treads are on, every finished step equals the unit rise. Assumes bare floor at both ends — adjust if there’s finish flooring.
      </p>
    </div>
  );
}

// ─── Cutlist view ───────────────────────────────────────────────────────────────

function cutlistToText(plan: StairPlan): string {
  const c = calcStairs(plan);
  const cut = generateStairCutlist(plan);
  const lines: string[] = [];
  lines.push(`${plan.name} — Stair Cutlist`);
  lines.push(`Rise ${formatInches(plan.totalRise)} · ${c.numRisers} risers @ ${formatInches(c.riserHeight)} · ${c.numTreads} treads @ ${formatInches(plan.treadDepth)}`);
  lines.push(`Total run ${formatInches(c.totalRun)} · Stringer ${formatInches(c.stringerLength)} · ${round1(c.angleDeg)}°`);
  lines.push("");
  lines.push("CUT SHEET");
  lines.push(`  Framing square: ${formatInches(c.riserHeight)} rise × ${formatInches(c.treadDepth)} run`);
  lines.push(`  Notches: ${c.numRisers} (${c.numTreads} tread cuts)`);
  lines.push(`  Bottom drop / cut off: ${formatInches(c.bottomDrop)}`);
  lines.push(`  Bottom riser cut: ${formatInches(c.firstRiserCut)}`);
  lines.push(`  Top riser cut: ${formatInches(c.topRiserCut)}`);
  lines.push(`  Throat: ${formatInches(c.throat)}${c.throatOK ? "" : " (BELOW MIN)"}`);
  lines.push(`  Stringer: ${formatInches(c.stringerLength)} — order ${c.boardLengthFt}′ ${plan.stringerBoard}`);
  lines.push("");
  [...cut.stringers, ...cut.treads, ...cut.risers].forEach((p) => {
    lines.push(`${p.label}`);
    lines.push(`  ${p.detail}`);
  });
  lines.push("");
  lines.push("For reference only. Check your local code before you build.");
  return lines.join("\n").trim();
}

function CutlistView({ plan, onBack }: { plan: StairPlan; onBack: () => void }) {
  const { toast } = useToast();
  const c = useMemo(() => calcStairs(plan), [plan]);
  const cut = useMemo(() => generateStairCutlist(plan), [plan]);
  const sections: [string, StairCutPiece[]][] = [
    ["Stringers", cut.stringers],
    ["Treads", cut.treads],
    ["Risers", cut.risers],
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cutlistToText(plan));
      toast({ title: "Cutlist copied" });
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={onBack} style={backBtnStyle} className="hover:text-foreground transition-colors">
          <ChevronLeft style={{ width: "1.125rem", height: "1.125rem" }} />
          Editor
        </button>
        <Button size="sm" variant="outline" onClick={copy} style={{ height: "2.25rem" }}>
          <Copy style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.4rem" }} />
          Copy
        </Button>
      </div>

      <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", fontVariantNumeric: "tabular-nums" }}>
        {c.numRisers} risers @ {formatInches(c.riserHeight)} · {c.numTreads} treads @ {formatInches(plan.treadDepth)} · {round1(c.angleDeg)}°
      </div>

      {c.numRisers > 0 && <CutSheet plan={plan} />}

      {sections.every(([, items]) => items.length === 0) ? (
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>Enter a total rise to generate a cutlist.</p>
      ) : (
        sections.map(([title, items]) =>
          items.length === 0 ? null : (
            <div key={title}>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", borderBottom: "1px solid hsl(var(--border))", paddingBottom: "0.3rem", marginBottom: "0.4rem" }}>
                {title}
              </div>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.25rem", listStyle: "none", margin: 0, padding: 0 }}>
                {items.map((it) => (
                  <li key={it.id} style={{ padding: "0.5rem 0.625rem", backgroundColor: "hsl(var(--muted))", borderRadius: "0.4rem" }}>
                    <div style={{ fontWeight: 700, fontSize: "0.9375rem" }}>{it.label}</div>
                    <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: "0.15rem", fontVariantNumeric: "tabular-nums" }}>{it.detail}</div>
                  </li>
                ))}
              </ul>
            </div>
          )
        )
      )}

      <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", lineHeight: 1.5, marginTop: "0.25rem" }}>
        <strong>For reference only.</strong> Check riser and tread limits, throat depth and headroom against your local code. Cut one stringer first and test-fit it before cutting the rest.
      </p>
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: "0.375rem",
  fontSize: "0.9375rem", color: "hsl(var(--muted-foreground))",
  background: "none", border: "none", cursor: "pointer", padding: "0.25rem 0",
};

// ─── Plan editor ──────────────────────────────────────────────────────────────

function PlanEditor({
  plan, onBack, onUpdate, onDelete,
}: {
  plan: StairPlan;
  onBack: () => void;
  onUpdate: (updates: Partial<Omit<StairPlan, "id" | "createdAt" | "updatedAt">>) => void;
  onDelete: () => void;
}) {
  const [tab, setTab] = useState<"layout" | "config">("layout");
  const [showCutlist, setShowCutlist] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const calc = useMemo(() => calcStairs(plan), [plan]);

  if (showCutlist) {
    return <CutlistView plan={plan} onBack={() => setShowCutlist(false)} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <button onClick={onBack} style={backBtnStyle} className="hover:text-foreground transition-colors">
          <ChevronLeft style={{ width: "1.125rem", height: "1.125rem" }} />
        </button>
        <Input
          value={plan.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Stair name"
          style={{ fontSize: "1.0625rem", fontWeight: 600, height: "2.75rem", flex: 1 }}
        />
      </div>

      <StairPreview plan={plan} />
      <CodeCheck calc={calc} />
      <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", lineHeight: 1.5, margin: 0 }}>
        <strong>For reference only.</strong> Check your local code before you build.
      </p>

      {/* Sub-tabs */}
      <div style={{ display: "flex", backgroundColor: "hsl(var(--muted))", padding: "0.25rem", borderRadius: "0.5rem", gap: "0.25rem" }}>
        {([["layout", "Layout"], ["config", "Config"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              flex: 1, padding: "0.5rem", fontSize: "0.8125rem", fontWeight: 600,
              borderRadius: "0.375rem", border: "none", cursor: "pointer",
              backgroundColor: tab === key ? "hsl(var(--background))" : "transparent",
              color: tab === key ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
              boxShadow: tab === key ? "var(--shadow-xs)" : "none",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "layout" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <DimInput label="Total Rise (floor to floor)" valueInches={plan.totalRise} onChange={(v) => onUpdate({ totalRise: v })} min={1} max={400} />
          <NumStepper label="Tread Depth (run)" value={plan.treadDepth} onChange={(v) => onUpdate({ treadDepth: v })} min={6} max={20} step={0.25} />
          <NumStepper label="Target Riser Height" value={plan.targetRiser} onChange={(v) => onUpdate({ targetRiser: v })} min={4} max={9} step={0.25} />

          <div style={{ borderTop: "1px solid hsl(var(--border))", paddingTop: "1rem" }}>
            <ResultTiles calc={calc} />
            <Button onClick={() => setShowCutlist(true)} variant="outline" className="w-full" style={{ height: "2.75rem", marginTop: "0.75rem" }}>
              <ClipboardList style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
              Cut Sheet & Materials
            </Button>
          </div>
        </div>
      )}

      {tab === "config" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <NumStepper label="Tread Thickness (sets bottom drop)" value={treadThicknessOf(plan)} onChange={(v) => onUpdate({ treadThickness: v })} min={0} max={3} step={0.25} />
          <DimInput label="Stair Width" valueInches={plan.stairWidth} onChange={(v) => onUpdate({ stairWidth: v })} min={24} max={120} />
          <NumStepper label="Tread Nosing" value={plan.nosing} onChange={(v) => onUpdate({ nosing: v })} min={0} max={1.5} step={0.25} />
          <NumStepper label="Number of Stringers" value={plan.stringerCount} onChange={(v) => onUpdate({ stringerCount: v })} min={2} max={6} step={1} unit="" integer />

          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", display: "block", marginBottom: "0.5rem" }}>
              Stringer Board
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
              {(["2x10", "2x12"] as StringerBoard[]).map((b) => (
                <Button key={b} variant={plan.stringerBoard === b ? "default" : "outline"} style={{ height: "2.75rem" }} onClick={() => onUpdate({ stringerBoard: b })}>
                  {b}
                </Button>
              ))}
            </div>
            <p style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", margin: "0.4rem 0 0", lineHeight: 1.5 }}>
              Board width sets the throat depth shown to scale above — a steep pitch on a 2×10 can fall below the {MIN_THROAT}″ minimum.
            </p>
          </div>

          <div style={{ borderTop: "1px solid hsl(var(--border))", paddingTop: "1rem" }}>
            {confirmDelete ? (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.8125rem", flex: 1, color: "hsl(var(--muted-foreground))" }}>Delete this stair?</span>
                <Button size="sm" variant="destructive" style={{ height: "2.25rem" }} onClick={onDelete}>Delete</Button>
                <Button size="sm" variant="ghost" style={{ height: "2.25rem" }} onClick={() => setConfirmDelete(false)}>Cancel</Button>
              </div>
            ) : (
              <Button variant="outline" className="w-full" style={{ height: "2.75rem", color: "hsl(var(--destructive))" }} onClick={() => setConfirmDelete(true)}>
                <Trash2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
                Delete Stair
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Top-level: list + editor ─────────────────────────────────────────────────

function fmtDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function StairStringer() {
  const { plans, addPlan, updatePlan, deletePlan } = useStairPlans();
  const [editingId, setEditingId] = useState<string | null>(null);

  const editing = editingId ? plans.find((p) => p.id === editingId) ?? null : null;
  const prevEditing = useRef(editingId);
  useEffect(() => {
    if (editingId && !plans.some((p) => p.id === editingId) && prevEditing.current === editingId) {
      setEditingId(null);
    }
    prevEditing.current = editingId;
  }, [editingId, plans]);

  const handleNew = () => {
    const id = addPlan({
      name: `Stair ${plans.length + 1}`,
      totalRise: 108,
      targetRiser: 7.5,
      treadDepth: 10.5,
      treadThickness: 1,
      nosing: 1,
      stairWidth: 36,
      stringerCount: 3,
      stringerBoard: "2x12",
    });
    setEditingId(id);
  };

  if (editing) {
    return (
      <PlanEditor
        plan={editing}
        onBack={() => setEditingId(null)}
        onUpdate={(u) => updatePlan(editing.id, u)}
        onDelete={() => { deletePlan(editing.id); setEditingId(null); }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Button onClick={handleNew} className="w-full" style={{ height: "3rem" }}>
        <Plus style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
        New Stair Layout
      </Button>

      {plans.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "hsl(var(--muted-foreground))" }}>
          <Footprints style={{ width: "2rem", height: "2rem", margin: "0 auto 0.5rem", opacity: 0.3 }} />
          <p style={{ fontSize: "0.875rem" }}>No stair layouts yet. Tap New Stair Layout to start.</p>
        </div>
      ) : (
        [...plans].sort((a, b) => b.updatedAt - a.updatedAt).map((plan) => {
          const c = calcStairs(plan);
          return (
            <button
              key={plan.id}
              onClick={() => setEditingId(plan.id)}
              style={{
                width: "100%", textAlign: "left", padding: "0.875rem 1rem",
                border: "1px solid hsl(var(--border))", borderRadius: "0.625rem",
                background: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "0.75rem",
              }}
              className="hover:bg-muted transition-colors"
            >
              <Footprints style={{ width: "1.25rem", height: "1.25rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.9375rem", color: "hsl(var(--foreground))" }}>{plan.name}</div>
                <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", fontVariantNumeric: "tabular-nums" }}>
                  {formatInches(plan.totalRise)} rise · {c.numRisers} risers @ {formatInches(c.riserHeight)}
                </div>
              </div>
              <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }}>{fmtDate(plan.updatedAt)}</span>
            </button>
          );
        })
      )}
    </div>
  );
}
